import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Editorial-dark badges. Used for statuses and small chips.
 * Uppercase tracking-wide variant is opt-in via `status` className combo.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-[#ff5c35]/15 text-[#ff8a6c]",
        secondary:
          "border-zinc-800 bg-zinc-900 text-zinc-300",
        outline:
          "border-zinc-800 text-zinc-400",
        destructive:
          "border-transparent bg-rose-500/15 text-rose-300",
        success:
          "border-transparent bg-emerald-500/15 text-emerald-300",
        warning:
          "border-transparent bg-amber-500/15 text-amber-300",
        info:
          "border-transparent bg-blue-500/15 text-blue-300",
        muted:
          "border-zinc-800/60 bg-zinc-900/60 text-zinc-500",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

/** Status pill — uppercase tracking-wide, h-5. Use for entity statuses. */
function StatusBadge({
  status,
  className,
}: {
  status: string;
  className?: string;
}) {
  const tone = mapStatusTone(status);
  return (
    <Badge
      variant={tone}
      className={cn(
        "h-5 px-1.5 uppercase tracking-wider text-[10px] font-semibold",
        className,
      )}
    >
      {status}
    </Badge>
  );
}

function mapStatusTone(
  status: string,
): "success" | "warning" | "destructive" | "muted" | "secondary" | "info" {
  const s = status.toLowerCase();
  if (s === "open" || s === "active" || s === "approved") return "success";
  if (s === "closed" || s === "pending" || s === "draft") return "warning";
  if (s === "cancelled" || s === "rejected" || s === "banned") return "destructive";
  if (s === "finished" || s === "archived") return "muted";
  return "secondary";
}

export { Badge, StatusBadge, badgeVariants }
